<template>
  <preloader-overlay :loading="loading">
    <div :class="$style.container">

      <div :class="$style.header">
        <responsive-controller>
          <template #tablet>
            <screen-header
              v-m-b="30"
              :title="$t('payout.crypto.header.title')"
              :history-mode="false"
              @navigate="$router.push({ name: 'cabinet' })"
            />
          </template>
          <template #default>
            <div v-m-b="30">
              <navigate-button
                v-absolute
                v-left="-80"
                @navigate="$router.push({ name: 'cabinet' })"
              />
              <screen-title
                :title="$t('payout.crypto.header.title')"
              />
            </div>
          </template>
        </responsive-controller>

        <payout-header
          :balance="displayBalance"
          :available="displayAvailableForPayout"
        />
      </div>

      <div
        v-if="getLinkedWallet || walletPayoutPermissionIsEnabled"
        v-m-b="50"
        :class="$style.content"
      >
        <template v-if="isDisabledPayoutPermission">
          <selectable-wallet-panel
            v-m-b="20"
            v-cursor:pointer
            :wallet="getLinkedWallet"
            @click="$router.push({ name: 'cardSelect' })"
          >
            <template v-slot:action="{ disabled }">
              <simple-button
                size="small"
                variant="darken"
                :disabled="disabled"
              >
                {{ $t('common.change') }}
              </simple-button>
            </template>
          </selectable-wallet-panel>

          <cs-switcher
            v-model="form.autoPayout"
            v-m-b="20"
            :label="$t('payout.form.auto.payout.label')"
            @input="handleChangeAutoPayout"
          />

          <div
            v-if="form.autoPayout"
            v-html="$t('payout.form.auto.payout.message', { amount: displayMaxPayoutAmount })"
          ></div>
        </template>

        <labeled-input
          v-else
          v-model="form.hash"
          v-m-b="20"
          :label="$t('wallet.binding.form.hash.label')"
          :placeholder="$t('wallet.binding.form.hash.placeholder')"
          :disabled="loading"
          :error="$v.form.hash.$error"
          @focus="$v.form.hash.$reset"
          @blur="$v.form.hash.$touch"
        />

        <div
          v-if="!form.autoPayout"
        >
          <labeled-input
            v-model="form.amount"
            v-m-b="20"
            :label="$t('payout.form.amount.label', {
              currency: currency.sign,
            })"
            :placeholder="$t('payout.form.amount.placeholder', {
              amount: 0,
              currency: currency.sign,
            })"
            :disabled="loading"
            :error="$v.form.amount.$error"
            :force-replace="[
              [/[^0-9\.\,]+/g, ''],
              [/^\./g, ''],
              [/\,/, '\.'],
            ]"
            :force-format="[$formatInputAmount({
              significantZeros: currency.significant_zeros,
              decimalsSeparator: '.',
              thousandSeparator: ' ',
            })]"
            @focus="$v.form.amount.$reset"
            @blur="$v.form.amount.$touch"
          />

          <div v-text="$t('payout.form.commission.label')"></div>

          <div
            v-if="commission.loading"
            v-text="$t('payout.form.commission.message.process')"
          ></div>

          <div
            v-if="form.amount && !commission.loading"
            v-text="displayCommisionMessage"
          ></div>
          <div
            v-else-if="!commission.loading"
            v-text="$t('payout.form.commission.message.before')"
          ></div>
        </div>
      </div>

      <div
        v-if="!form.autoPayout"
        :class="$style.footer"
      >
        <div
          v-if="isShownError"
          v-m-t="50"
          :class="$style.limits"
        >
          <div v-html="displayError"></div>
          <div
            v-m-t="20"
            v-text-decoration:underline
            v-cursor:pointer
            @click="handleReduceAmount"
            v-text="$t('reduce.amount')"
          ></div>
        </div>
        <div
          v-else
          :class="$style.submit"
        >
          <simple-button
            :disabled="isInvalid"
            varinat="primary"
            @click="handleSubmit"
          >
            <div v-d-flex="{ direction: 'column' }">
              <div v-text="$t('payout.form.submit.label', { amount: displayPayoutAmount })"></div>
              <div v-text="$t('payout.form.submit.message')"></div>
            </div>
          </simple-button>
        </div>
      </div>
    </div>
  </preloader-overlay>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';

import {
  required as requiredValdiator,
  minLength as minLengthValidator
} from 'vuelidate/lib/validators';

// TODO: @danealton Избавиться и переписать
import { isLessMaxAmount, isMoreMinAmount } from '@/components/inputElements/CustomValidators';

import { formatAmount, normalizeAmount } from 'lib/utils/amount';
import throttle from 'lib/utils/throttle';

import ScreenTitle from 'molecules/screen/ScreenTitle';
import ScreenHeader from 'molecules/screen/ScreenHeader';
import NavigateButton from 'molecules/common/NavigateButton';
import PreloaderOverlay from 'molecules/common/PreloaderOverlay';
import LabeledInput from 'molecules/common/LabeledInput';
import SimpleButton from 'molecules/common/SimpleButton';

import PayoutHeader from 'organisms/payout/PayoutHeader';
import SelectableWalletPanel from 'organisms/wallet/SelectableWalletPanel';

const HASH_MIN_LENGTH = 35;

export default {
  name: 'PayoutCrypto',
  components: {
    ScreenTitle,
    ScreenHeader,
    NavigateButton,
    PreloaderOverlay,
    LabeledInput,
    SimpleButton,
    PayoutHeader,
    SelectableWalletPanel,
  },
  created() {
    this.initialize();
  },
  data() {
    return {
      loading: false,
      form: {
        amount: '',
        autoPayout: false,
        hash: '',
      },
      commission: {
        loading: false,
      },
    };
  },
  validations() {
    if (this.walletPayoutPermissionIsEnabled) {
      return {
        form: {
          amount: {
            required: requiredValdiator,
            minValue: isMoreMinAmount(this.minPayoutAmount, this.currency),
            maxValue: isLessMaxAmount(this.maxAvailableAmount, 0, this.currency),
          },
          hash: {
            required: requiredValdiator,
            minLength: minLengthValidator(HASH_MIN_LENGTH),
          },
        },
      };
    }

    return {
      form: {
        amount: {
          required: requiredValdiator,
          minValue: isMoreMinAmount(this.minPayoutAmount, this.currency),
          maxValue: isLessMaxAmount(this.maxAvailableAmount, 0, this.currency),
        },
      },
    };
  },
  computed: {
    isInvalid() {
      if (this.commission.loading) {
        return true;
      }

      return this.$v.form.$invalid;
    },
    currency() {
      return this.PROJECT_INFO.currency;
    },
    displayBalance() {
      const { balance } = this.PROJECT_INFO;

      return formatAmount(balance, this.currency);
    },
    displayAvailableForPayout() {
      const { availableForPayout } = this.PROJECT_INFO;

      return formatAmount(availableForPayout, this.currency);
    },
    displayMaxPayoutAmount() {
      const availablePayoutAmounts = this.payoutDetails?.maxPayoutAmount ?? 0;

      return formatAmount(availablePayoutAmounts, this.currency);
    },
    isDisabledPayoutPermission() {
      return !this.walletPayoutPermissionIsEnabled;
    },
    countryPrefix() {
      const countryMap = {
        'RU': this.$t('common.russia'),
        'UA': 'UA',
      };

      if (countryMap[this.payoutDetails?.alpha_2]) {
        return `${countryMap[this.payoutDetails?.alpha_2]} - `;
      }

      return null;
    },
    displayCommisionMessage() {
      return this.$t('payout.form.commission.message.after', {
        amount: this.displayMinimalComission,
        percent: this.payoutDetails?.commissionPercent ?? 0,
      });
    },
    displayMinimalComission() {
      const promoDetails = this.payoutDetails?.promoDetails ?? null;
      const minimalCommission = this.payoutDetails?.minimalCommission ?? 0;

      if (this.$validate.isNull(promoDetails)) {
        return formatAmount(minimalCommission, this.currency);
      }

      return formatAmount(promoDetails.addedMinimalCommission, this.currency);
    },
    displayPayoutAmount() {
      return formatAmount(this.totalPayoutSum, this.currency);
    },
    payoutCommission() {
      if (!this.$validate.isObjectWithFields(this.payoutDetails, ['promoDetails', 'commission'])) {
        return 0;
      }

      const { promoDetails, commission } = this.payoutDetails;

      if (this.$validate.isNull(promoDetails)) {
        return commission;
      }

      return promoDetails.addedCommission || 0;
    },
    normalizePayoutSum() {
      return normalizeAmount(this.form.amount, this.currency) ?? 0;
    },
    totalPayoutSum() {
      return this.normalizePayoutSum > 0 ? this.normalizePayoutSum + this.payoutCommission : 0;
    },
    minAvailableAmount() {
      return (this.payoutDetails?.minAvailableAmount ?? 0) + this.payoutCommission;
    },
    maxAvailableAmount() {
      return (this.payoutDetails?.maxAvailableAmount ?? 0) + this.payoutCommission;
    },
    minPayoutAmount() {
      return this.payoutDetails?.minPayoutAmount ?? 0;
    },
    maxPayoutAmount() {
      return this.payoutDetails?.maxPayoutAmount ?? 0;
    },
    monthlyAvailablePayoutAmount() {
      return this.payoutDetails?.monthlyAvailablePayoutAmount ?? 0;
    },
    monthlyPayoutAmount() {
      return this.payoutDetails?.monthlyPayoutAmount ?? 0;
    },
    isBalanceLower() {
      const { availableForPayout } = this.PROJECT_INFO;

      return availableForPayout < this.minPayoutAmount || availableForPayout < this.normalizePayoutSum;
    },
    isShownError() {
      // веденная сумма > суммы на балансе
      if (this.normalizePayoutSum > this.PROJECT_INFO.availableForPayout) {
        return true;
      }

      // отсутствует доступная для вывода сумма или количество попыток
      // if (this.isEmptyPayoutCount || this.isEmptyPayoutAmount) {
      //   return true;
      // }

      // введенная сумма + комиссия > остатка по месячному лимиту
      if (this.totalPayoutSum > this.monthlyAvailablePayoutAmount) {
        return true;
      }

      // введенная сумма < минимальной суммы вывода
      if (this.normalizePayoutSum < (this.minPayoutAmount + this.payoutCommission)) {
        return false;
      }

      // введенная сумма + комиссия > максимальная сумма на вывод и она входит в месячный лимит
      if (this.totalPayoutSum > this.maxAvailableAmount) {
        return this.totalPayoutSum <= this.monthlyPayoutAmount;
      }

      return false;
    },
    displayError() {
      // Сумма недоступна для вывода.
      if (this.isBalanceLower) {
        return this.$t('not.enough.funds');
      }

      // недостаточно средств по месячному лимиту
      // if (this.isEmptyPayoutAmount) {
      //   return this.$t('payout.monthly.available.limit', {
      //     amount: this.displayMonthlyPayoutAmount,
      //     link: '/wallet/select',
      //   });
      // }

      // достингут лимит количенства выводов по месячному лимиту
      // if (this.isEmptyPayoutCount) {
      //   return this.$t('payout.count.available.limit', {
      //     count: this.displayPayoutCount,
      //     link: '/wallet/select',
      //   });
      // }

      // введенная сумма + комиссия > максимальная сумма на вывод и она входит в месячный лимит
      if (this.totalPayoutSum > this.maxAvailableAmount) {
        if (this.totalPayoutSum > this.maxPayoutAmount) {
          // Выше максимальной суммы на вывод.
          return this.$t('more.then.maximum.amount.for.payout');
        }

        // введенная сумма + комиссия > остатка по месячному лимиту
        if (this.totalPayoutSum > this.monthlyAvailablePayoutAmount) {
          return this.$t('payout.monthly.available.limit', {
            amount: this.displayMonthlyPayoutAmount,
            link: '/wallet/select',
          });
        }
      }

      // Выше максимальной суммы на вывод.
      return this.$t('more.then.maximum.amount.for.payout');
    },
    displayMonthlyPayoutAmount() {
      return formatAmount(this.monthlyPayoutAmount, this.currency);
    },
    hashOrWalletId() {
      return this.walletPayoutPermissionIsEnabled ? this.form.hash : this.getLinkedWallet?.id;
    },
    walletHash() {
      return this.walletPayoutPermissionIsEnabled ? this.form.hash : this.getLinkedWallet?.hash;
    },
    ...mapState([
      'PROJECT_INFO',
    ]),
    ...mapState('project', [
      'payoutDetails',
      'availablePayoutAmounts',
    ]),
    ...mapGetters([
      'walletPayoutPermissionIsEnabled',
    ]),
    ...mapGetters('wallet', [
      'isEmptyWalletList',
      'isNoLinkedWallet',
      'isSingleAvailableWallet',
      'isLinkedWalletInBlacklisted',
      'getLinkedWallet',
    ]),
  },
  watch: {
    'form.amount': {
      handler: 'throttleLoadCommission',
    },
  },
  methods: {
    async initialize() {
      this.loading = true;

      await this.fetchWalletList();

      if (this.walletPayoutPermissionIsEnabled) {
        console.log('Разрешение на выплаты без привяки кошелька включено');

        this.loading = false;
        return;
      }

      console.log('Разрешение на выплаты без привяки кошелька отключено');

      if (this.isEmptyWalletList) {
        console.log('Привязанных кошельков не обнаружено');

        this.$router.push({ name: 'wallet.binding' });
        return;
      }

      if (this.isNoLinkedWallet) {
        console.log('Кошелек на вывод не установлен', 'отправить на выбор другого кошелька');

        this.$router.push({ name: 'wallet.select' });
        return;
      }

      if (this.isLinkedWalletInBlacklisted) {
        console.log('Кошелек на вывод в черном списке');

        if (this.isSingleAvailableWallet) {
          console.log('Это единственный кошелек на вывод', 'отправить на привязку нового кошелька');
          this.$router.push({ name: 'wallet.binding' });
        } else {
          console.log('Есть еще кошельки на вывод', 'отправить на выбор другого кошелька');
          this.$router.push({ name: 'wallet.select' });
        }

        return;
      }

      console.log('Кошелек на вывод корректен', 'разрешен доступ на вывод средств');
      this.form.autoPayout = this.PROJECT_INFO.autoPayout === 'ENABLED';

      await this.getPayoutDetails({
        amount: this.normalizePayoutSum,
        hash: this.walletHash,
      });

      this.loading = false;
    },
    async loadCommission() {
      this.commission.loading = true;

      try {
        await this.getPayoutDetails({
          amount: this.normalizePayoutSum,
          hash: this.walletHash,
        });
      } finally {
        this.commission.loading = false;
      }
    },
    // eslint-disable-next-line prefer-arrow-callback
    throttleLoadCommission: throttle(async function () {
      await this.loadCommission();
    }, 500),
    async handleChangeAutoPayout() {
      if (!this.getLinkedWallet) {
        return;
      }

      const isChecked = this.form.autoPayout;

      const requestData = {
        ...this.PROJECT_INFO,
        autoPayout: isChecked ? 'ENABLED' : 'DISABLED',
      };

      try {
        await this.updateProject(requestData);

        this.$alert({
          type: 'cabbage',
          text: isChecked ? 'common.autopayout.on' : 'common.autopayout.off',
        });
      } catch {
        this.$alert({
          type: 'warning',
          text: 'server.error',
        });
      }
    },
    async handleSubmit() {
      if (this.isInvalid) {
        this.$alert({
          type: 'warning',
          text: 'mistakes.in.form',
        });
        return;
      }

      this.commission.loading = true;

      const parameterName = this.walletPayoutPermissionIsEnabled ? 'account' : 'walletId';

      const requestData = {
        currency: this.PROJECT_INFO.currency,
        amount: this.normalizePayoutSum,
        [parameterName]: this.hashOrWalletId,
      };

      try {
        await this.createPayout(requestData);

        this.$alert({
          type: 'cabbage',
          text: 'money.transfered.to.card',
        });

        this.$router.push({ name: 'cabinet' });
      } catch (error) {
        console.log(error);

        // const { status } = error.data;

        // if (error.status === 400 && status === 'PS_DECLINE') {
        //   this.isShownErrorPayout = true;
        //   return;
        // }

        // if (error.status === 400 && status === 'TRANSACTION_LIMITS_EXCEEDED') {
        //   this.$alert({
        //     type: 'warning',
        //     text: 'payout.card.limits.error',
        //   });
        //   return;
        // }

        this.$alert({
          type: 'warning',
          text: 'server.error',
        });
      } finally {
        this.commission.loading = false;
      }
    },
    async handleReduceAmount() {
      await this.loadCommission();

      const maxAvailableAmount = this.payoutDetails?.maxAvailableAmount ?? 0;

      this.form.amount = formatAmount(maxAvailableAmount, this.currency);
    },
    ...mapActions('project', [
      'updateProject',
      'getPayoutDetails',
      'createPayout',
    ]),
    ...mapActions('wallet', [
      'fetchWalletList',
    ]),
  },
};
</script>

<style lang="sass" module>
  .container
    +size(100%, auto)
    max-width: 500px
    margin: 0 auto
    padding: 20px 0 60px

    +responsive('large')
      padding: 60px 0

  .header
    position: relative
    padding: 0 20px

    +respond-to('small')
      padding: 0

  .content
    position: relative
    padding: 20px
    background-color: var(--color-dark-blue)

  .submit
    width: 100%
    max-width: 300px
    margin: 0 auto

  .limits
    +font-type('regular', 18, 24)
    color: var(--color-white)
    text-align: left
</style>
